import * as React from "react";
import { FileText, Folder } from "lucide-react";
import { useDraggable } from "@dnd-kit/core";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { formatDate } from "@/lib/format";
import { DocumentPreviewTooltip } from "./DocumentPreviewTooltip";

type DocumentStatus = "draft" | "published" | "archived";

type CheckboxCheckedState = boolean | "indeterminate";

type DocumentRowProps = {
  type: "document" | "folder";
  title: string;
  status?: DocumentStatus;
  updatedAt?: number;
  documentCount?: number;
  contentLength?: number;
  summary?: string;
  contentPreview?: string;
  tags?: string[];
  onClick: () => void;
  onDoubleClick?: () => void;
  documentId?: string;
  isSelected?: boolean;
  onSelectionChange?: (selected: boolean) => void;
};

function statusBadgeClass(status: DocumentStatus): string {
  if (status === "published") return "border-emerald-300 text-emerald-700 bg-emerald-50";
  if (status === "archived") return "border-zinc-300 text-zinc-700 bg-zinc-50";
  return "border-amber-300 text-amber-700 bg-amber-50";
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function DocumentRow({
  type,
  title,
  status,
  updatedAt,
  documentCount,
  contentLength,
  summary,
  contentPreview,
  tags,
  onClick,
  onDoubleClick,
  documentId,
  isSelected,
  onSelectionChange,
}: DocumentRowProps) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: documentId || `folder-${title}`,
    data: {
      type,
      documentId,
      title,
    },
    disabled: type === "folder",
  });
  const { role: _dragRole, tabIndex: _dragTabIndex, ...dragAttributes } = attributes;

  const handleClick = React.useCallback((e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('[role="checkbox"]')) {
      return;
    }
    onClick();
  }, [onClick]);

  const handleDoubleClick = React.useCallback(() => {
    if (onDoubleClick) {
      onDoubleClick();
      return;
    }
    onClick();
  }, [onClick, onDoubleClick]);

  const handleKeyDown = React.useCallback((event: React.KeyboardEvent) => {
    if ((event.target as HTMLElement).closest('[role="checkbox"]')) {
      return;
    }
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    onClick();
  }, [onClick]);

  const handleCheckboxChange = React.useCallback((checked: CheckboxCheckedState) => {
    if (onSelectionChange) {
      onSelectionChange(checked === true);
    }
  }, [onSelectionChange]);

  const rowContent = (
    <tr
      ref={setNodeRef}
      className={`cursor-pointer border-b transition-colors hover:bg-muted/50 ${
        isDragging ? "opacity-50" : ""
      }`}
      role="button"
      tabIndex={0}
      aria-label={type === "folder" ? `Open folder ${title}` : `Open document ${title}`}
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}
      onKeyDown={handleKeyDown}
      {...(type === "document" ? listeners : {})}
      {...(type === "document" ? dragAttributes : {})}
    >
      <td className="py-3 pl-4 pr-3">
        <div className="flex items-center gap-3">
          {type === "document" && onSelectionChange && (
            <Checkbox
              checked={isSelected}
              onCheckedChange={handleCheckboxChange}
              onClick={(event) => event.stopPropagation()}
              onPointerDown={(event) => event.stopPropagation()}
              onKeyDown={(event) => event.stopPropagation()}
              aria-label={`Select ${title}`}
            />
          )}
          <div className="rounded-md border p-1.5">
            {type === "folder" ? (
              <Folder className="size-4 text-amber-600" />
            ) : (
              <FileText className="size-4 text-blue-600" />
            )}
          </div>
          <span className="font-medium">{title}</span>
        </div>
      </td>
      <td className="px-3 py-3">
        {status && (
          <Badge variant="outline" className={statusBadgeClass(status)}>
            {status}
          </Badge>
        )}
        {type === "folder" && documentCount !== undefined && (
          <Badge variant="outline" className="text-xs text-muted-foreground">
            {documentCount} {documentCount === 1 ? "doc" : "docs"}
          </Badge>
        )}
      </td>
      <td className="px-3 py-3 text-sm text-muted-foreground">
        {updatedAt ? formatDate(updatedAt, { month: "short", day: "numeric", year: "numeric" }) : "—"}
      </td>
      <td className="px-3 py-3 pr-4 text-sm text-muted-foreground">
        {type === "document" && contentLength ? formatFileSize(contentLength) : "—"}
      </td>
    </tr>
  );

  if (type === "document" && contentPreview) {
    return (
      <DocumentPreviewTooltip
        title={title}
        summary={summary}
        contentPreview={contentPreview}
        status={status}
        tags={tags}
        updatedAt={updatedAt}
      >
        {rowContent}
      </DocumentPreviewTooltip>
    );
  }

  return rowContent;
}
